import { Account } from "models/Account";
import { Invoice } from "models/Invoice";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { Alert, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { bindActionCreators } from "redux";
import * as api from "services/Api";
import { addInvoice } from "store/actions/Billing";
import { getTenants } from "store/actions/Tenants";
import { TenantState } from "store/reducers/Tenants";
import { RootState } from "store/store";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

interface Props {
    tenantState: TenantState;
}

interface Actions {
    addInvoice: (invoice: Invoice) => void;
    getTenants: () => void;
}

export const invoiceDeadlineTypeForAccount = (account: Account): "fixed" | "pay_when_paid" =>
    account.billing.default_deadline_type === "pay_when_paid" ? "pay_when_paid" : "fixed";

const InvoiceAddButton: React.FC<Props & Actions> = ({
    tenantState,
    addInvoice,
    getTenants,
}) => {
    const { t } = useOfficeTranslation();
    const navigate = useNavigate();
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [customerId, setCustomerId] = useState("");
    const [supplierId, setSupplierId] = useState("");
    const [open, setOpen] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.getAccounts().then(setAccounts);
        getTenants();
    }, [getTenants]);

    const close = () => {
        setOpen(false);
        setError("");
    };

    const createNew = (isInvoice: boolean) => {
        const customer = accounts.find(account => account.id === customerId);
        if (!customer) {
            setError(t("invoice.invoiceAddButton.chooseACustomer.e590cbb"));
            return;
        }

        const invoice = new Invoice();
        invoice.draft = true;
        invoice.quote = !isInvoice;
        invoice.created = new Date();
        const deadline = new Date();
        deadline.setDate(deadline.getDate() + customer.billing.default_deadline);
        invoice.deadline = deadline;
        invoice.deadline_type = invoiceDeadlineTypeForAccount(customer);
        invoice.tenant = customer.tenant;
        invoice.customer = customer.id;
        invoice.vat_reverse_charged = customer.billing.default_vat_reverse_charged ?? false;
        if (supplierId) {
            invoice.supplier = supplierId;
        }

        // @ts-ignore Redux thunk resolves with the created invoice.
        addInvoice(invoice).then(response => {
            close();
            navigate(`/invoice/${response.id}`);
        });
    };

    return (
        <>
            <button
                className="btn btn-primary"
                onClick={() => setOpen(true)}
                type="button"
            >
                <i className="mdi mdi-plus me-1" />
                {t("invoice.invoiceAddButton.newInvoiceQuote")}
            </button>

            <Modal centered isOpen={open} toggle={close}>
                <ModalHeader toggle={close}>{t("invoice.invoiceAddButton.newInvoiceOrQuote")}</ModalHeader>
                <ModalBody>
                    {error && <Alert color="danger">{error}</Alert>}

                    <label className="form-label" htmlFor="invoice-customer">{t("common.customer")}</label>
                    <Select
                        inputId="invoice-customer"
                        options={accounts.map(account => ({
                            value: account.id,
                            label: `${account.legal_name} - ${tenantState.getTenantById(account.tenant)?.name ?? ""}`,
                        }))}
                        placeholder={t("invoice.invoiceAddButton.chooseACustomer")}
                        onChange={(option: any) => {
                            setCustomerId(option?.value ?? "");
                            setError("");
                        }}
                    />

                    <label className="form-label mt-3" htmlFor="invoice-supplier">
                        {t("common.supplier")}
                    </label>
                    <Select
                        inputId="invoice-supplier"
                        isClearable
                        options={accounts.map(account => ({
                            value: account.id,
                            label: account.legal_name,
                        }))}
                        placeholder={t("invoice.invoiceAddButton.tenantDefault")}
                        onChange={(option: any) => setSupplierId(option?.value ?? "")}
                    />
                    <small className="d-block text-muted mt-2">
                        {t("invoice.invoiceAddButton.paymentDetailsAlwaysComeFromTheTenant")}
                    </small>
                </ModalBody>
                <ModalFooter>
                    <button className="btn btn-light" onClick={close} type="button">
                        {t("common.cancel")}
                    </button>
                    <button className="btn btn-outline-primary" onClick={() => createNew(false)} type="button">
                        {t("invoice.invoiceAddButton.createQuote")}
                    </button>
                    <button className="btn btn-primary" onClick={() => createNew(true)} type="button">
                        {t("invoice.invoiceAddButton.createInvoice")}
                    </button>
                </ModalFooter>
            </Modal>
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    tenantState: state.Tenants,
});

const mapDispatchToProps = (dispatch: any) =>
    bindActionCreators({ addInvoice, getTenants }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceAddButton);
